/**
 * Utility functions for processing SSE streams
 */

/**
 * Check if a string is a plain text error message
 * @param {string} text - Text to check
 * @returns {boolean} True if text appears to be an error message
 */
// TODO: This is a temporary function to check errors and display them in the chat for testing purposes.
// We should get consensus if we want to keep this function or not.
function isPlainTextError(text) {
  if (!text || typeof text !== 'string') return false;
  
  // Check for common error patterns
  const errorPatterns = [
    /^Error while running agent:/i,
    /^Error \d{3}/i,
    /Resource exhausted/i,
    /RESOURCE_EXHAUSTED/i,
    /failed to call model:/i,
    /Please try again later/i
  ];
  
  return errorPatterns.some(pattern => pattern.test(text));
}

/**
 * Check if a JSON string is incomplete (missing closing braces/quotes)
 * Uses proper JSON parsing instead of regex to accurately detect incomplete JSON.
 * This is the correct approach: try to parse and check the error type.
 * 
 * @param {string} jsonStr - JSON string to validate
 * @returns {boolean} True if JSON appears incomplete (should wait for more chunks)
 */
function isIncompleteJSON(jsonStr) {
  if (!jsonStr || typeof jsonStr !== 'string') return false;
  
  const trimmed = jsonStr.trim();
  
  // Empty string is not incomplete JSON
  if (trimmed.length === 0) return false;
  
  // Try to parse the JSON - this is the proper way to validate JSON
  try {
    JSON.parse(trimmed);
    // If parsing succeeds, JSON is complete
    return false;
  } catch (error) {
    // If parsing fails, check if it's due to incomplete JSON
    if (error instanceof SyntaxError) {
      const errorMessage = error.message.toLowerCase();
      
      // These error messages indicate incomplete JSON (waiting for more data)
      // This is the standard way browsers/engines report incomplete JSON
      const incompleteIndicators = [
        'unexpected end of json input',
        'unexpected end of input',
        'unexpected end of data',
        'unexpected end of string'
      ];
      
      // Primary check: if the error message indicates incomplete JSON
      if (incompleteIndicators.some(indicator => errorMessage.includes(indicator))) {
        return true;
      }
      
      // Secondary heuristic: check if the string appears to be in the middle of a JSON structure
      // This handles edge cases where the error message might vary
      // Only apply this if we have a clear opening structure
      const hasOpeningBrace = trimmed.includes('{');
      const hasOpeningBracket = trimmed.includes('[');
      
      if (hasOpeningBrace || hasOpeningBracket) {
        // Check if it ends with characters that suggest it's incomplete
        // (not a closing brace/bracket, but something that suggests more is coming)
        const lastNonWhitespace = trimmed.match(/\S$/)?.[0];
        const incompleteEndings = [',', ':', '{', '['];
        
        // If it ends with an incomplete indicator and has opening structures,
        // it's likely incomplete (waiting for closing)
        if (lastNonWhitespace && incompleteEndings.includes(lastNonWhitespace)) {
          return true;
        }
        
        // Check if we're in the middle of a string (odd number of unescaped quotes)
        // This is more reliable than regex for detecting incomplete strings
        let inString = false;
        let escapeNext = false;
        for (let i = 0; i < trimmed.length; i++) {
          const char = trimmed[i];
          if (escapeNext) {
            escapeNext = false;
            continue;
          }
          if (char === '\\') {
            escapeNext = true;
            continue;
          }
          if (char === '"') {
            inString = !inString;
          }
        }
        
        // If we're still in a string at the end, JSON is incomplete
        if (inString) {
          return true;
        }
      }
    }
    
    // If it's not a SyntaxError or doesn't match incomplete patterns, it's likely malformed
    // (not incomplete, so we should error rather than wait)
    return false;
  }
}

/**
 * Check if a parsed object represents an empty tool response
 * Only flags as empty if it's a COMPLETE tool call result, not intermediate chunks.
 * During streaming, tool calls may start without results - that's normal.
 * 
 * @param {Object} data - Parsed data object
 * @returns {boolean} True if this appears to be a complete empty tool response
 */
function isEmptyToolResponse(data) {
  if (!data || typeof data !== 'object') return false;
  
  // Only check for empty responses if this is explicitly a tool call RESULT/END
  // Don't flag tool call START events as empty - they're expected to have no result initially
  
  // Check for tool call result with empty/null/undefined result
  // Only flag if we have a result field that's explicitly empty (not just missing)
  const hasToolCallId = data.toolCallId || data.tool_call_id || data.toolCall || data.tool_call;
  if (hasToolCallId) {
    // Check if this is a result/end event (has result field present, even if null/empty)
    const hasResultField = 'result' in data || 
                          'toolCallResult' in data || 
                          'tool_call_result' in data || 
                          'response' in data;
    
    // Only flag as empty if result field exists AND is empty
    // If result field doesn't exist, it might be a START event (normal during streaming)
    if (hasResultField) {
      const result = data.result || data.toolCallResult || data.tool_call_result || data.response;
      // Only flag if result is explicitly null, empty string, or empty object
      // undefined means the field doesn't exist, which is fine for START events
      if (result === null || result === '' || 
          (typeof result === 'object' && result !== null && Object.keys(result).length === 0)) {
        return true;
      }
    }
  }
  
  // Check for functionResponse with empty response
  // Only flag if functionResponse exists AND response is explicitly empty
  // During streaming, functionResponse might exist without response yet
  if (data.content?.parts) {
    for (const part of data.content.parts) {
      if (part?.functionResponse) {
        // Check if response field exists (not just undefined)
        if ('response' in part.functionResponse) {
          const response = part.functionResponse.response;
          // Only flag if response is explicitly null, empty string, or empty object
          if (response === null || 
              (typeof response === 'string' && response.trim() === '') ||
              (typeof response === 'object' && response !== null && Object.keys(response).length === 0)) {
            return true;
          }
        }
        // If response field doesn't exist, it might be in progress - don't flag as empty
      }
    }
  }
  
  return false;
}

/**
 * Extract error code from plain text error message
 * @param {string} text - Error message text
 * @returns {string|null} Error code if found, null otherwise
 */
function extractErrorCodeFromText(text) {
  if (!text) return null;
  
  // Extract 429 from "Error 429"
  const error429Match = text.match(/Error (\d{3})/i);
  if (error429Match) {
    return error429Match[1];
  }
  
  // Check for RESOURCE_EXHAUSTED
  if (text.includes('RESOURCE_EXHAUSTED') || text.includes('Resource exhausted')) {
    return '429';
  }
  
  return null;
}

/**
 * Process SSE response and handle events
 * @param {Response} response - Fetch response object
 * @param {Object} handlers - Event handlers and state setters
 * @param {Function} handlers.processSSEEvent - Function to process individual SSE events
 * @param {Function} handlers.setIsStreaming - Function to set streaming state
 * @param {Object} handlers.allEventsRef - Ref to track all events for debugging
 * @param {Function} handlers.onStreamStart - Optional callback when stream starts (first event received)
 * @param {Function} handlers.onStreamEnd - Optional callback when stream ends
 * @param {Function} handlers.onStreamError - Optional callback when stream error is detected
 */
export async function processSSEStream(response, { processSSEEvent, setIsStreaming, allEventsRef, onStreamStart, onStreamEnd, onStreamError }) {
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let streamActive = true; // Track if stream connection is open
  let hasReceivedData = false; // Track if we've received any data
  let lastValidData = null; // Track last successfully parsed data
  let incompleteBuffer = ''; // Track incomplete JSON chunks

  try {
    if (onStreamStart) {
      onStreamStart();
    }

    while (streamActive) {
      const { done, value } = await reader.read();
      
      // Check for connection loss (done without proper stream end)
      if (done) {
        streamActive = false;
        
        // If we have incomplete JSON in buffer, it's a partial response
        if (incompleteBuffer && isIncompleteJSON(incompleteBuffer)) {
          const errorEvent = {
            error: 'Connection lost. Received partial response. Please try again.',
            errorCode: 'PARTIAL_RESPONSE',
            status: 'PARTIAL_RESPONSE',
            timestamp: Date.now(),
            isPartialResponse: true
          };
          if (onStreamError) {
            onStreamError(errorEvent);
          }
          processSSEEvent(errorEvent);
        }
        // If we never received any data, it's a connection issue
        else if (!hasReceivedData) {
          const errorEvent = {
            error: 'Connection lost. No response received. Please try again.',
            errorCode: 'CONNECTION_LOST',
            status: 'CONNECTION_LOST',
            timestamp: Date.now(),
            isConnectionLost: true
          };
          if (onStreamError) {
            onStreamError(errorEvent);
          }
          processSSEEvent(errorEvent);
        }
        
        if (onStreamEnd) {
          onStreamEnd();
        }
        break;
      }

      hasReceivedData = true;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || ''; // Keep incomplete line in buffer

      for (const line of lines) {
        if (line.trim() === '') continue;

        if (line.startsWith('data: ')) {
          try {
            const dataStr = line.slice(6).trim();
            
            if (dataStr === '[DONE]') {
              streamActive = false;
              if (onStreamEnd) {
                onStreamEnd();
              }
              break;
            }

            // Check if this is a plain text error message (not JSON)
            if (isPlainTextError(dataStr)) {
              // Create error event from plain text
              const errorEvent = {
                error: dataStr,
                errorCode: extractErrorCodeFromText(dataStr),
                status: dataStr.includes('RESOURCE_EXHAUSTED') ? 'RESOURCE_EXHAUSTED' : null,
                timestamp: Date.now()
              };
              if (onStreamError) {
                onStreamError(errorEvent);
              }
              processSSEEvent(errorEvent);
              continue;
            }

            // Check if JSON is incomplete before parsing
            if (isIncompleteJSON(dataStr)) {
              incompleteBuffer = dataStr;
              // Don't process incomplete JSON, wait for more chunks
              // But if this is the last chunk and stream ends, we'll handle it above
              continue;
            }

            // Clear incomplete buffer if we have complete JSON
            incompleteBuffer = '';

            const rawData = JSON.parse(dataStr);
            
            lastValidData = rawData;

            // Note: We don't check for empty tool responses during streaming because:
            // 1. During streaming, tool calls may start without results (normal)
            // 2. Intermediate chunks may appear empty but are just in progress
            // 3. Checking on every chunk causes false positives
            // Empty tool response detection should be handled at the event level
            // (in adkEventParser or useChatStream) after the stream completes

            // Process the event
            processSSEEvent(rawData);
          } catch (e) {
            // If JSON parsing fails, check if it's an error message or incomplete JSON
            const dataStr = line.slice(6).trim();
            
            if (isPlainTextError(dataStr)) {
              const errorEvent = {
                error: dataStr,
                errorCode: extractErrorCodeFromText(dataStr),
                status: dataStr.includes('RESOURCE_EXHAUSTED') ? 'RESOURCE_EXHAUSTED' : null,
                timestamp: Date.now()
              };
              if (onStreamError) {
                onStreamError(errorEvent);
              }
              processSSEEvent(errorEvent);
            } else if (isIncompleteJSON(dataStr)) {
              incompleteBuffer = dataStr;
              // Store incomplete buffer for later check when stream ends
            } else {
              // Other JSON parse errors - might be malformed JSON
              const errorEvent = {
                error: 'Received malformed response. Please try again.',
                errorCode: 'MALFORMED_RESPONSE',
                status: 'MALFORMED_RESPONSE',
                timestamp: Date.now(),
                isMalformedResponse: true,
                parseError: e.message
              };
              if (onStreamError) {
                onStreamError(errorEvent);
              }
              processSSEEvent(errorEvent);
            }
          }
        } else if (line.startsWith('event: ')) {
          // Handle SSE event types
          const eventType = line.slice(7).trim();
          if (eventType === 'end') {
            streamActive = false;
            if (onStreamEnd) {
              onStreamEnd();
            }
            break;
          } else if (eventType === 'error') {
            streamActive = false;
            const errorEvent = {
              error: 'Stream error occurred. Please try again.',
              errorCode: 'STREAM_ERROR',
              status: 'STREAM_ERROR',
              timestamp: Date.now(),
              isStreamError: true
            };
            if (onStreamError) {
              onStreamError(errorEvent);
            }
            processSSEEvent(errorEvent);
            if (onStreamEnd) {
              onStreamEnd();
            }
            break;
          }
        }
      }
    }
  } catch (error) {
    streamActive = false;
    
    // Create error event for connection/processing errors
    const errorEvent = {
      error: error.message || 'Connection error occurred. Please try again.',
      errorCode: 'STREAM_PROCESSING_ERROR',
      status: 'STREAM_PROCESSING_ERROR',
      timestamp: Date.now(),
      isStreamProcessingError: true,
      originalError: error.message
    };
    
    if (onStreamError) {
      onStreamError(errorEvent);
    }
    processSSEEvent(errorEvent);
    
    // Re-throw to be handled by the caller
    throw error;
  } finally {
    // Ensure streaming state is cleared and stream is marked as ended
    if (streamActive) {
      streamActive = false;
    }
    if (setIsStreaming) {
      setIsStreaming(false);
    }
    if (onStreamEnd) {
      onStreamEnd();
    }
    try {
      reader.releaseLock();
    } catch (e) {
      // Reader may already be released
    }
  }
}
